import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { clsx } from '../utils/utils';

const AccordionContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  border-radius: var(--radius-small);
  overflow: hidden;

  & + & {
    margin-top: var(--bigger-size);
  }
`;

const AccordionBox = styled.div`
  width: 100%;
  background: var(--tts-mid-grey);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
`;

const TitleBox = styled.button`
  transition: all 0.3s ease;
  width: 100%;
  padding: var(--small-size) var(--x-small-size);
  background-color: transparent;
  display: flex;
  justify-content: space-between;
  gap: var(--smallest-size);
  align-items: center;
  border: none;
  cursor: pointer;
`;

const IconContainer = styled.div`
  position: relative;
  margin: 0 var(--smallest-size);
`;

const IconVerticalLine = styled.div`
  height: 16px;
  width: 2px;
  background-color: var(--tts-black);
  transition: transform 0.5s ease-in-out;
  transform: rotate(-90deg);

  .accordionOpen & {
    transition: all 0.5s ease-in-out;
    transform: rotate(90deg);
  }
`;

const IconHorizontalLine = styled.div`
  position: absolute;
  top: 7px;
  right: -7px;
  height: 2px;
  width: 16px;
  background-color: var(--tts-black);
  transition: transform 0.5s ease-in-out;
  transform: rotate(-90deg);
  opacity: 1;

  .accordionOpen & {
    transition: all 0.5s ease-in-out;
    transform: rotate(90deg);
    opacity: 0;
  }
`;

const Title = styled.div`
  font-size: var(--text-heading-size);
  text-align: left;
`;

const Content = styled.div`
  padding: var(--smallest-size);
  padding-top: 0;

  p:first-child {
    margin-top: 0;
  }
`;

const Body = styled.div`
  transition: all 0.3s ease-out;
  width: 100%;
  background-color: var(--tts-mid-grey);
  overflow: hidden;
  visibility: visible;

  &.hidden {
    visibility: hidden;
  }
`;

interface AccordionProps {
  title: string;
  children: React.ReactNode;
  startsOpen?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

const Accordion = ({
  title,
  children,
  startsOpen,
  onOpen,
  onClose,
}: AccordionProps): React.ReactElement => {
  const [isAccordionOpen, setIsAccordionOpen] = useState(true);

  const [bodyRef, setBodyRef] = useState<HTMLDivElement | null>(null);

  const bodyMaxHeight = isAccordionOpen && bodyRef ? bodyRef.scrollHeight : 0;

  useEffect(() => {
    if (!startsOpen) {
      setIsAccordionOpen(false);
    }
  }, [startsOpen]);

  return (
    <AccordionContainer className={isAccordionOpen && 'accordionOpen'}>
      <AccordionBox>
        <TitleBox
          className={'titleBox'}
          aria-expanded={isAccordionOpen}
          aria-disabled="false"
          role="button"
          tabIndex={0}
          onClick={() => {
            setIsAccordionOpen(!isAccordionOpen);
            if (isAccordionOpen) {
              if (onClose) {
                onClose();
              }
            } else {
              if (onOpen) {
                onOpen();
              }
            }
          }}
        >
          <Title>{title}</Title>
          <IconContainer>
            <IconVerticalLine />
            <IconHorizontalLine />
          </IconContainer>
        </TitleBox>
        <Body
          ref={newRef => {
            setBodyRef(newRef);
          }}
          className={!isAccordionOpen && 'hidden'}
          style={{ maxHeight: bodyMaxHeight }}
          role="region"
          aria-hidden={!isAccordionOpen}
        >
          <Content>{children}</Content>
        </Body>
        <noscript>
          <div
            className={clsx('body', !isAccordionOpen && 'hidden')}
            role="region"
            aria-hidden={!isAccordionOpen}
          >
            <div className={'content'}>{children}</div>
          </div>
        </noscript>
      </AccordionBox>
    </AccordionContainer>
  );
};

export default Accordion;
